import { Dialog, DialogContent, DialogTitle, Divider, Grid } from '@mui/material';
import {
  useNotify,
  useRecordContext,
  useRefresh,
  Button,
  SaveButton,
  SaveContextProvider,
  SimpleForm,
  TextInput,
  Toolbar,
  required,
  useDataProvider,
  ReferenceInput,
  AutocompleteInput,
} from 'react-admin';
import { FieldValues, useFormContext } from 'react-hook-form';
import { useState } from 'react';

import { createDialogFormContext } from 'src/components/DialogFormContext';
import { multilineIds, zodIssuesIntoErrorsMap } from 'src/utils/validation';
import z from 'zod';

export const { provider: AddProgramUsersDialogContextProvider, useContext: useAddProgramUsersDialogContext } =
  createDialogFormContext();

const validationSchema = z.object({ userIds: multilineIds });

const MAX_PROGRAMS_PER_USER = 10;

const CustomToolbar = ({ onCancel }: { onCancel: () => void }) => (
  <Toolbar>
    <Grid container justifyContent="space-between">
      <Button label="Cancel" onClick={onCancel} />
      <SaveButton variant="contained" type="button" />
    </Grid>
  </Toolbar>
);

const Controls = ({ currentProgramId }: { currentProgramId: string | number }) => {
  const { setValue, getValues } = useFormContext();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const [loading, setLoading] = useState(false);

  const handleImport = async () => {
    const programToImportId = getValues('programToImportId');
    if (!programToImportId) {
      notify('Please select a program to import from.', { type: 'warning' });
      return;
    }

    setLoading(true);
    try {
      const res = await dataProvider.getList('users_programs', {
        meta: { noLimit: true },
        sort: { field: 'created_at', order: 'DESC' },
        filter: { 'program_id@_eq': programToImportId },
      });

      const userIds = (res.data as Array<{ user_id: string | number }>).map((r) => String(r.user_id));
      const existingList = getValues('userIds') ? getValues('userIds').split(/\n+/).filter(Boolean) : [];
      const combined = Array.from(new Set([...existingList, ...userIds]));

      setValue('userIds', combined.join('\n'), { shouldDirty: true, shouldValidate: true });
      notify(`Imported ${userIds.length} user${userIds.length !== 1 ? 's' : ''}.`, { type: 'info' });
    } catch (e) {
      notify('Failed to import users from the selected program.', { type: 'warning' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid container spacing={1} alignItems="top" sx={{ mb: 1 }}>
      <Grid item xs={8}>
        <ReferenceInput
          source="programToImportId"
          reference="programs"
          perPage={50}
          filter={{ 'id@_neq': currentProgramId, 'deleted_at@_is_null': true }}
          label="Import users from"
        >
          <AutocompleteInput
            optionText="name"
            optionValue="id"
            fullWidth
            filterToQuery={(q: string) => {
              const maybeNum = parseInt(q, 10);
              if (!Number.isNaN(maybeNum)) {
                return { id: q };
              }
              return { name: q };
            }}
          />
        </ReferenceInput>
      </Grid>
      <Grid item xs={4} mt={1}>
        <Button label="Import users" onClick={handleImport} disabled={loading} size="small" />
      </Grid>
    </Grid>
  );
};

const UserSearch = () => {
  const { setValue, getValues } = useFormContext();
  const notify = useNotify();

  const handleSelect = (_event: unknown, selected: unknown) => {
    if (selected === null || selected === undefined) return;

    const id = String(
      typeof selected === 'object' && selected !== null && (selected as Record<string, unknown>).id
        ? (selected as Record<string, unknown>).id
        : selected,
    );
    const existingList = getValues('userIds') ? String(getValues('userIds')).split(/\n+/).filter(Boolean) : [];

    if (!existingList.includes(id)) {
      const combined = [...existingList, id];
      setValue('userIds', combined.join('\n'), { shouldDirty: true, shouldValidate: true });
      notify(`Added user ${id}`, { type: 'info' });
    } else {
      notify(`User ${id} is already in the list`, { type: 'warning' });
    }

    setTimeout(() => {
      setValue('userSearch', '', { shouldDirty: false, shouldValidate: false });
    }, 0);
  };

  return (
    <Grid container spacing={1} alignItems="top" sx={{ mb: 1 }}>
      <Grid item xs={12}>
        <ReferenceInput
          source="userSearch"
          reference="users"
          perPage={25}
          sort={{ field: 'name', order: 'ASC' }}
          filter={{ 'deleted_at@_is_null': true }}
          label="Search for user by name or email"
        >
          <AutocompleteInput
            optionText={(r: {
              id?: string | number;
              name?: string;
              first_name?: string;
              last_name?: string;
              email?: string;
              nickname?: string;
            }) => {
              const first = r?.first_name?.trim?.();
              const last = r?.last_name?.trim?.();
              const full = [first, last].filter(Boolean).join(' ').trim();
              const email = r?.email;
              if (full && email) return `${full} (${email})`;
              if (full) return full;
              if (r?.name && email) return `${r.name} (${email})`;
              return r?.name || email || String(r?.id ?? '');
            }}
            optionValue="id"
            fullWidth
            onChange={handleSelect}
            filterToQuery={(q: string) => ({
              'name@_ilike,first_name@_ilike,last_name@_ilike,nickname@_ilike,email@_ilike': q,
            })}
            placeholder="Type a name to search and add"
          />
        </ReferenceInput>
      </Grid>
    </Grid>
  );
};

export const AddProgramUsersDialog = () => {
  const dialogCtx = useAddProgramUsersDialogContext();
  const program = useRecordContext<{ id: string | number }>();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();

  const handleSubmit = async (rawValues: FieldValues) => {
    if (!program?.id) return undefined;

    const validationResult = validationSchema.safeParse(rawValues);
    if (!validationResult.success) return zodIssuesIntoErrorsMap(validationResult.error.issues);

    const values = validationResult.data;
    const uniqueUserIds = [...new Set(values.userIds as string[])];

    // 1) Filter out users that are already in THIS program
    const existing = await dataProvider.getList('users_programs', {
      pagination: { page: 1, perPage: uniqueUserIds.length || 1 },
      sort: { field: 'created_at', order: 'DESC' },
      filter: { 'program_id@_eq': program.id, 'user_id@_in': uniqueUserIds },
    });
    const existingSet = new Set((existing.data as Array<{ user_id: string | number }>).map((r) => String(r.user_id)));

    const toCreate = uniqueUserIds.filter((id) => !existingSet.has(String(id)));

    if (toCreate.length === 0) {
      notify('No users added: all provided users are already in the program.', { type: 'info' });
      dialogCtx.setClosed();
      return undefined;
    }

    // 2) Enforce max programs per user across ALL programs
    const allMemberships = await dataProvider.getList('users_programs', {
      pagination: { page: 1, perPage: Math.max(toCreate.length * 10, 10) },
      sort: { field: 'created_at', order: 'DESC' },
      filter: { 'user_id@_in': toCreate },
    });

    const membershipCounts = (allMemberships.data as Array<{ user_id: string | number }>).reduce<
      Record<string, number>
    >((acc, r) => {
      const uid = String(r.user_id);
      acc[uid] = (acc[uid] ?? 0) + 1;
      return acc;
    }, {});

    const allowedToCreate = toCreate.filter((uid) => (membershipCounts[String(uid)] ?? 0) < MAX_PROGRAMS_PER_USER);
    const overLimitUsers = toCreate.filter((uid) => (membershipCounts[String(uid)] ?? 0) >= MAX_PROGRAMS_PER_USER);

    if (overLimitUsers.length) {
      notify(`${overLimitUsers.map((uid) => `User ${uid} already over the program limit`).join('\n')}`, {
        type: 'warning',
      });
    }

    if (allowedToCreate.length === 0) {
      notify('No users added: all provided users are already at the program limit.', { type: 'warning' });
      dialogCtx.setClosed();
      return undefined;
    }

    const results = await Promise.allSettled(
      allowedToCreate.map((userId) =>
        dataProvider.create('users_programs', { data: { program_id: program.id, user_id: userId } }),
      ),
    );

    const successCount = results.filter((r) => r.status === 'fulfilled').length;
    const failCount = results.length - successCount;

    if (successCount) {
      notify(`${successCount} user${successCount > 1 ? 's were' : ' was'} added to the program.`, { type: 'success' });
    }
    if (failCount) {
      notify(`${failCount} user${failCount > 1 ? 's' : ''} failed to add.`, { type: 'warning' });
    }

    refresh();
    dialogCtx.setClosed();
    return undefined;
  };

  return (
    <Dialog open={dialogCtx.open} onClose={dialogCtx.setClosed} fullWidth>
      <DialogTitle>Add Users to Program</DialogTitle>
      <Divider />
      <DialogContent>
        <SaveContextProvider value={{ save: handleSubmit }}>
          <SimpleForm mode="onChange" toolbar={<CustomToolbar onCancel={dialogCtx.setClosed} />} mt={2} mx={1}>
            {program?.id && <Controls currentProgramId={program.id} />}
            <UserSearch />
            <TextInput
              label="User ID(s)"
              source="userIds"
              helperText={
                <>
                  Each user can be added to up to {MAX_PROGRAMS_PER_USER} programs. They will be automatically filtered
                  out if they are already at the limit together with duplicates and non-existing users.
                </>
              }
              multiline
              validate={required()}
              fullWidth
              minRows={15}
              maxRows={15}
              autoFocus
            />
          </SimpleForm>
        </SaveContextProvider>
      </DialogContent>
    </Dialog>
  );
};

export default AddProgramUsersDialog;
